import { CommonModule } from '@angular/common';
import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { InvoicesApiService } from '@anduin/api';
import { DialogSize, RecipientEmailsComponent, SharedModule, extractFailureContext, ignoreFalsy, removeDuplicates } from '@anduin/shared';
import { BehaviorSubject, Subject, catchError, combineLatest, first, map, of, switchMap, take, tap } from 'rxjs';
import { InvoicesStep, InvoicesTrackService } from '../../tracking';

export interface ResendInvoiceDialogData {
  invoiceId: number;
  invoiceNumber: string;
  invoiceStatus?: string;
}

@Component({
  selector: 'app-resend-invoice-dialog',
  templateUrl: './resend-invoice-dialog.component.html',
  styleUrl: './resend-invoice-dialog.component.scss',
  standalone: true,
  imports: [MatDialogModule, MatProgressSpinnerModule, CommonModule, SharedModule],
})
export class ResendInvoiceDialogComponent {
  @ViewChild(RecipientEmailsComponent, { static: false }) emailsComponent: RecipientEmailsComponent;

  dialogSize = DialogSize.MEDIUM;

  _isLoading = new BehaviorSubject<boolean>(true);
  isLoading$ = this._isLoading.asObservable();

  _isSending = new BehaviorSubject<boolean>(false);
  isSending$ = this._isSending.asObservable();

  _errors = new Subject<string | null>();
  errors$ = this._errors.asObservable();

  submitBtnEnabled = false;

  private submitted = false;

  public emailRecipientsVm$ = this.invoicesApiService.getInvoiceEmailRecipients(this.data.invoiceId.toString()).pipe(
    map((emailRecipients) => emailRecipients.map((recipient) => recipient.email)),
    map((emails) => removeDuplicates(emails)),
    catchError(() => {
      this._errors.next('Unable to retrieve original recipients for this invoice.');
      return of([]);
    }),
    tap((_) => this._isLoading.next(false)),
  );

  constructor(
    private readonly invoicesApiService: InvoicesApiService,
    private readonly dialogRef: MatDialogRef<ResendInvoiceDialogComponent>,
    private readonly trackService: InvoicesTrackService,
    @Inject(MAT_DIALOG_DATA) readonly data: ResendInvoiceDialogData,
  ) {
    // Any close without SUBMITTED is treated as user-cancel so analytics can distinguish
    // abandoned resends from completed attempts.
    this.dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        if (!this.submitted) {
          this.trackService.trackWorkflow(InvoicesStep.RESEND_CANCELLED, {
            invoiceId: this.data.invoiceId,
            invoiceStatus: this.data.invoiceStatus ?? '',
          });
        }
      });
  }

  submit(): void {
    this.submitted = true;
    const trackingPayload = {
      invoiceId: this.data.invoiceId,
      invoiceStatus: this.data.invoiceStatus ?? '',
    };
    this.trackService.trackWorkflow(InvoicesStep.RESEND_SUBMITTED, trackingPayload);
    const startedAt = performance.now();

    this.emailsComponent
      .validate()
      .pipe(
        first(),
        ignoreFalsy(),
        tap((_) => this._isSending.next(true)),
        switchMap(() =>
          combineLatest([this.emailsComponent.emitPrimaryEmailChanges$, this.emailsComponent.emitAdditionalEmailsChanges$]).pipe(first()),
        ),
        switchMap(([primary, additional]) => {
          const emails = [...[primary], ...additional];
          return this.invoicesApiService.resendInvoice(this.data.invoiceId, emails).pipe(
            tap((_) =>
              this.trackService.trackWorkflow(InvoicesStep.RESEND_SUCCEEDED, {
                ...trackingPayload,
                operationDurationMs: performance.now() - startedAt,
              }),
            ),
            map((_) => true),
            catchError((err: unknown) => {
              this.trackService.trackWorkflow(InvoicesStep.RESEND_FAILED, {
                ...trackingPayload,
                ...extractFailureContext(err),
                operationDurationMs: performance.now() - startedAt,
              });
              this._errors.next('Unable to resend this invoice.  Please try again shortly.');
              // Dialog stays open after failure so the user can retry or cancel. Re-open the cancel
              // funnel by clearing `submitted` — otherwise a post-failure close emits no terminal event.
              this.submitted = false;
              return of(false);
            }),
          );
        }),
        tap((_) => this._isSending.next(false)),
      )
      .subscribe((sent: boolean) => {
        if (sent) {
          this._errors.next(null);
          this.dialogRef.close(true);
        }
      });
  }

  setSubmitBtnValidity(disabled: boolean): void {
    this.submitBtnEnabled = !disabled;
  }
}
